import React from 'react';
import { Link } from 'react-router-dom';

// Common components
import HtmlContent from '../components/common/HtmlContent';
import useHashScroll from '../hooks/useHashScroll';

/**
 * DesignPage Component
 * 
 * Displays all design content just like the legacy HTML site
 * Follows the same structure as ResearchPage.js
 */
const DesignPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();

  // Design intro content
  const designIntroHtml = `
    <p>
      The research phase was big! But it meant that we have ensured to the best of our ability that we are designing a product that has a significant enough need to continue down this path.
    </p>
    <p>
      In the Design phase we cover the process of visually designing an interactive solution that can hopefully fulfil the need of the end user.
    </p>
    <p>
      This solution can come in many shapes, it may be a literal version 0.1 or 1.0, or a simply a Minimum Viable/Value Product - something that can not only further test the demand for a solution, but test if the envisaged solution is the right direction.
    </p>
  `;

  // Ideation section content
  const ideationHtml = `
    <p>
      The ideation phase is where we begin to have more fun (particularly after the sometimes relentless feeling of the research phase!) this is where we get to stretch our creative muscles, this is where we begin to craft our solution.
    </p>
    <p>
      Inspired by our Research, and encompassing a few techniques, the aim for the Ideation phase is to come out with a rough design of how to solve the problem - the design is then finessed as we proceed further along our path ultimately bringing it towards implementation.
    </p>
    <h4>Ideation Tools</h4>
  `;

  return (
    <>
      {/* Design Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/">R&D Toolkit</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/design">Design Tools</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/design">Design</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={designIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Tool Sections</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#ideation">Ideation</Link></div>
            <div><a className="tool-section-cta" href="#experimenting">Experimenting [...soon]</a></div>
            <div><a className="tool-section-cta" href="#structure">Structure [...soon]</a></div>
            <div><a className="tool-section-cta" href="#visual-design">Visual Design [...soon]</a></div>
          </div>
        </div>
      </div>

      {/* Ideation Section */}
      <main className="case-study">
        <span className="case-tool-title" id="ideation">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="ideation"><span className="tool-title-number"><Link to="/design/ideation/">Ideation</Link></span></div>
          <div className="tool-title-nav"><Link to="/">R&D Toolkit</Link>&nbsp;&nbsp;<i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;<Link to="/design/">Design</Link></div>
          <div className="tool-title-link"><Link to="/design/ideation/"><i className="fas fa-link fa-2x"></i></Link></div>
        </div>
        <div className="linebreak"></div>
        <div id="tool-box-styling-grandparent" className="tool-box-styling">
          <div className="tool-opening-container">
            <div className="tool-opening">
              <HtmlContent html={ideationHtml} containerClass="" />
            </div>
          </div>
        </div>
        <div id="tabs-grandparent" className="tabs">
          {/* Warm Up Tab */}
          <input type="radio" name="tabs-ideation" id="tab-ideation-warm-up" defaultChecked />
          <label htmlFor="tab-ideation-warm-up">Warm Up</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/warm-up/#lightning-demos" className="link-table">Lightning Demos</Link></td>
                  <td>A good warm up exercise for showcasing how other organisations have solved similar problems.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/warm-up/lightning-demos.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM-evNdw=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/warm-up/#grid" className="link-table">Grid</Link></td>
                  <td>A joint exercise of imaging something from a description, and then visualising it through sketching - both designed to encourage creativity.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/warm-up/grid.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVMC05IMs=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/warm-up/#alternate-uses" className="link-table">Alternate Uses</Link></td>
                  <td>A method of looking at things from a different perspective, emphasising that existing solutions can solve different problems, and that not all problems have to be solved the same way.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/warm-up/alternate-uses.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM9j5WGs=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/warm-up/#crazy-8s" className="link-table">Crazy 8s</Link></td>
                  <td>Where the ideation phase starts to come in to it's own - Crazy 8s begins the solution searching phase.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/warm-up/crazy-8s.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVMB6th1g=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Sketching Tab */}
          <input type="radio" name="tabs-ideation" id="tab-ideation-sketching" />
          <label htmlFor="tab-ideation-sketching">Sketching</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/sketching/#overview" className="link-table">Sketching Overview</Link></td>
                  <td>What is Sketching? Why do we do it? What is it used for? This overview should help clarify the purpose and potential.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/sketching/sketching-example.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM-tIRDI=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/sketching/#visual-alphabet" className="link-table">Visual Alphabet</Link></td>
                  <td>By breaking down images in to basic symbols, the visual alphabet continues the quick process of Sketching, whilst introducing commonality across a team.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/sketching/visual-alphabet.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM9ZBfTk=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/sketching/#ui-icons" className="link-table">UI Icons</Link></td>
                  <td>A collection of common UI Icons that can be used in the sketching process to show functionality.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/sketching/ui-icons.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM9cz73I=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/sketching/#sketch-log" className="link-table">Sketch Log</Link></td>
                  <td>Capture those ideas and designs by logging them - they may come in handy for inspiration in the future.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/sketching/sketch-log.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM804ARU=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/sketching/#ai-sketching" className="link-table">AI Sketching</Link></td>
                  <td>If the purpose of sketching is rapidly developing ideas, then AI is an additional tool that can further speed up the process.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/sketching/ai-sketching.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM7lvn5Y=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Storyboarding Tab */}
          <input type="radio" name="tabs-ideation" id="tab-ideation-storyboarding" />
          <label htmlFor="tab-ideation-storyboarding">Storyboarding</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/storyboarding/#concept" className="link-table">Concept</Link></td>
                  <td>An overview to help understand Storyboarding and why it is useful and relevant when designing a new product or feature.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/storyboarding/storyboarding-examples.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVMwDonCA=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/storyboarding/#outcome-statement" className="link-table">Outcome Statement</Link></td>
                  <td>An Outcome Statement goes hand in hand with Storyboarding by detailing the situation, problem, potential solution and desired outcome.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/storyboarding/outcome-statement.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM7UNW1I=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/storyboarding/#the-storyboard" className="link-table">The Storyboard</Link></td>
                  <td>Let's structure the Storyboard and detail the process using the narrative arc.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/storyboarding/the-storyboard.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVM6h5QYo=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/storyboarding/#ai-storyboarding" className="link-table">AI Storyboarding</Link></td>
                  <td>Following the same process as creating a normal Storyboard, an AI storyboard potentially supercharges the process.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/storyboarding/ai-storyboarding.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/o9J_kpGUsJk=/" className="link-table">Miro</a>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Design Studio Tab */}
          <input type="radio" name="tabs-ideation" id="tab-ideation-design-studio" />
          <label htmlFor="tab-ideation-design-studio">Design Studio</label>
          <div id="tab-no-sideborder" className="tab tab-tools">
            <table id="tool-table">
              <colgroup className="tool-table-columns">
                <col className="tool-table-columns-20" />
                <col className="tool-table-columns-60" />
                <col className="tool-table-columns-20" />
              </colgroup>
              <tbody>
                <tr className="table-section-tools">
                  <td>Tool</td>
                  <td>Description</td>
                  <td>Template</td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#the-facilitator" className="link-table">The Facilitator</Link></td>
                  <td>Who is the Facilitator, what is their role and how to facilitate a Design Studio.</td>
                  <td></td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#participants" className="link-table">Participants</Link></td>
                  <td>Covers the participants and their roles that are usually included in the workshop.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/design-studio/participants.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/board/uXjVMvt7o1U=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#the-session" className="link-table">The Session</Link></td>
                  <td>Details what is expected from the session and in what order.</td>
                  <td>
                    <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
                    <a href="/design/ideation/design-studio/design-studio-guide.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
                    <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
                    <a href="https://miro.com/app/embed/uXjVM3BS0Vs=/" className="link-table">Miro</a>
                  </td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#research-synthesis" className="link-table">Research Synthesis</Link></td>
                  <td>This is where we go over what has been discovered so far, and how it will guide us with our design direction.</td>
                  <td></td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#ideation" className="link-table">Ideation</Link></td>
                  <td>The creative process begins, and this is how we guide it.</td>
                  <td></td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#feature-prioritisation" className="link-table">Feature Prioritisation</Link></td>
                  <td>Towards the end of the session, and after much discussion, the group can list and then prioritise features.</td>
                  <td></td>
                </tr>
                <tr>
                  <td><Link to="/design/ideation/design-studio/#outro" className="link-table">Outro</Link></td>
                  <td>The wrap up of the session, including revisiting the goals, archiving what has been produced from the session, and deciding on next steps.</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </>
  );
};

export default DesignPage; 